import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Close } from "@mui/icons-material";
import {
  Box,
  ClickAwayListener,
  Fade,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { AmplifySessionManager } from "../../api/AmplifySessionManager";
import { AppContext } from "../../App";
import { MicrophoneIcon, RefreshIcon, SendIcon } from "../../components/Icons/Icons";
import { SoundWave } from "../../components/Icons/SoundWave";
import { Thinking } from "../../components/Icons/Thinking";
import { useUser } from "../../components/Menu/hooks/useUser";
import { MODEL_NAME_POPUP_HELPER } from "../../config/modelConstants";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { useLocalStorage } from "../../core/useLocalStorage";
import { getMarkdownComponents, getMarkdownStyles } from "./MarkdownStyles.jsx";
import { VegaChart } from "./VegaChart.jsx";

const MAX_INPUT_LENGTH = 400;
export const PROMPT_NAME_DEFAULT = "tilley_my_farm_data";

export const ChatCommandHelper = ({
  promptName,
  userMessage,
  onChatCompletion,
  elapsedTime,
  listening,
  onStopRecording,
  onStartRecording,
  onClose,
  placeholder = "Fill out smart form...",
}) => {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [isWorking, setIsWorking] = useState(false);
  const [chatVoiceModeOn, setChatVoiceModeOn] = useLocalStorage("tilley_chat_voice_mode", false);
  const { attachments, onChangeRecords } = useChatAssistantAttachments();
  const [inputMessage, setInputMessage] = useState("");
  const [assistantResponse, setAssistantResponse] = useState("");
  const [showResponse, setShowResponse] = useState(false);
  const messageInputRef = useRef(null);
  const [userName, setUserName] = useState("");
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [lastSentMessage, setLastSentMessage] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Create custom markdown components that can render Vega charts
  const getCustomMarkdownComponents = () => ({
    ...getMarkdownComponents(theme),
    code: ({ inline, children, ...props }) => {
      if (inline) {
        return (
          <code className="inline-code" {...props}>
            {children}
          </code>
        );
      }

      const content = typeof children === "string" ? String(children).replace(/\n$/, "") : children;

      try {
        const spec = typeof content === "string" ? JSON.parse(content) : content;
        if (spec.$schema && spec.$schema.includes("vega-lite")) {
          // Responsive sizing: mobile gets compact, tablet gets medium, desktop gets HUGE!
          const width = isMobile ? 320 : isTablet ? 600 : 1000;
          const height = isMobile ? 240 : isTablet ? 400 : 600;
          const title = spec.title?.text || spec.title || "Chart";
          return <VegaChart spec={spec} width={width} height={height} title={title} />;
        }
      } catch {
        // Not valid JSON or not a Vega spec, fall back to regular code block
      }

      return (
        <pre className="code-block">
          <code {...props}>{content}</code>
        </pre>
      );
    },
  });

  const handleUserError = useCallback(
    (err) => {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
    },
    [enqueueSnackbar]
  );

  const { myUser, activeProducer, activeAgent } = useUser(
    {
      user_role: globalState.user_role,
      producer_token: globalState.producer_token,
      agent_token: globalState.agent_token,
      globalDispatch,
    },
    handleUserError
  );

  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  useEffect(() => {
    let userName;
    if (activeProducer?.producer_token) {
      userName = `${capitalizeFirstLetter(activeProducer?.first_name)} ${capitalizeFirstLetter(activeProducer?.middle_name)} ${capitalizeFirstLetter(activeProducer?.last_name)}`;
      if (userName.trim() === "") {
        userName = activeProducer?.business_name ?? "";
      }
    }
    if (userName === "" || !userName) {
      if (activeAgent) {
        userName = `${capitalizeFirstLetter(activeAgent?.first_name)} ${capitalizeFirstLetter(activeAgent?.middle_name)} ${capitalizeFirstLetter(activeAgent?.last_name)}`;
        if (userName.trim() === "") {
          userName = activeAgent?.business_name ?? "";
        }
      }
    }
    if (userName === "" || !userName) {
      userName = myUser?.name ?? "";
    }
    setUserName(userName);
  }, [activeProducer, activeAgent, myUser]);

  const validateAndSetInputMessage = (value) => {
    if (value.length > MAX_INPUT_LENGTH) {
      enqueueSnackbar(`You can enter only up to ${MAX_INPUT_LENGTH} characters`, {
        variant: "warning",
        preventDuplicate: true,
      });
      return value.slice(0, MAX_INPUT_LENGTH);
    }
    return value;
  };

  useEffect(() => {
    if (userMessage && userMessage.trim()) {
      const validatedMessage = validateAndSetInputMessage(userMessage);
      setInputMessage(validatedMessage);

      // Auto-submit if this was triggered by voice Send button
      if (shouldAutoSubmit) {
        setShouldAutoSubmit(false);
        setTimeout(() => {
          sendChatCompletion(validatedMessage);
        }, 100);
      }
    }
  }, [userMessage, shouldAutoSubmit]);

  // Focus input when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const handleError = (error, enqueueSnackbar, customMessage = null) => {
    console.error(error.consoleMessage || "API Error:", error);
    enqueueSnackbar(customMessage || error.userMessage || "A network or unexpected error occurred.", {
      preventDuplicate: true,
      variant: "error",
    });
  };

  const handleInputChange = useCallback((value) => {
    setInputMessage(validateAndSetInputMessage(value));
  }, []);

  const sendChatCompletion = async (_input, isRefresh = false) => {
    if (listening) {
      onStopRecording();
    }

    setIsWorking(true);

    if (isRefresh) {
      setIsRefreshing(true);
      // Don't clear the response or hide the panel when refreshing
    } else {
      // Store the message being sent
      setLastSentMessage(_input);
      // Don't clear input message immediately - keep it until after response
      setAssistantResponse(""); // Clear previous response
      setShowResponse(false);
    }

    const systemAttachments = [
      {
        name: "System Information",
        content: `Current Date = ${new Date().toLocaleString()}`,
        doPreload: true,
      },
    ];

    const userProfileAttachments = [
      {
        name: "User Profile",
        content: `User Name = ${userName}; User Role = ${globalState?.user_role}`,
        doPreload: true,
      },
    ];

    // Show response panel immediately for streaming
    if (!isRefresh) {
      setShowResponse(true);
    }

    try {
      const chatCompletion = await SendChatCompletion_api({
        messages: [{ role: "user", content: _input }],
        attachments: [...attachments, ...systemAttachments, ...userProfileAttachments],
        onChunk: (content) => {
          // Update assistant response incrementally as chunks arrive
          setAssistantResponse(content);
        },
      });

      if (!chatCompletion) {
        console.warn("Chat completion returned null.");
        setIsWorking(false);
        return null;
      }

      // Extract content from the response (reasoning already streamed via onChunk)
      const { content: chatContent } = chatCompletion;
      const cleanedString = chatContent?.replace(/```json|```/g, "").trim();
      let parsedData;
      let wasHandledAsStructuredData = false;

      try {
        parsedData = JSON.parse(cleanedString);

        // Check for navigation category in both array and single object cases
        const hasNavigation = (data) => {
          if (Array.isArray(data)) {
            return data.some((item) => item.category === "navigate");
          } else if (typeof data === "object" && data !== null) {
            return data.category === "navigate";
          }
          return false;
        };

        const getNavigationModule = (data) => {
          if (Array.isArray(data)) {
            const navItem = data.find((item) => item.category === "navigate");
            return navItem?.module;
          } else if (typeof data === "object" && data !== null && data.category === "navigate") {
            return data.module;
          }
          return null;
        };

        if (hasNavigation(parsedData)) {
          const module = getNavigationModule(parsedData);
          if (module) {
            // Navigate to /{module}
            navigate(`/${module}`);
            setAssistantResponse(`Navigating to ${module}...`);
            setShowResponse(true);
            return;
          }
        }

        if (parsedData.$schema && parsedData.$schema.includes("vega-lite")) {
          setAssistantResponse(`\`\`\`json\n${JSON.stringify(parsedData, null, 2)}\n\`\`\``);
          setShowResponse(true);
          return;
        }
      } catch {
        setAssistantResponse(cleanedString);
        setShowResponse(true);
        onChatCompletion?.(cleanedString);
        return;
      }

      if (Array.isArray(parsedData) && parsedData.length > 0) {
        const refreshedRecords = parsedData.map((item) => refreshObjectFromAttachments(item));
        if (onChangeRecords) {
          onChangeRecords(refreshedRecords);
        } else {
          onChatCompletion?.(refreshedRecords);
        }
        wasHandledAsStructuredData = true;
      } else if (typeof parsedData === "object" && parsedData !== null && Object.keys(parsedData).length > 0) {
        const refreshedRecords = refreshObjectFromAttachments(parsedData);
        if (onChangeRecords) {
          onChangeRecords([refreshedRecords]);
        } else {
          onChatCompletion?.([refreshedRecords]);
        }
        wasHandledAsStructuredData = true;
      } else {
        console.warn("Parsed JSON is empty or invalid format. Passing original string.");
        setAssistantResponse(cleanedString);
        setShowResponse(true);
        onChatCompletion?.(cleanedString);
      }

      // Don't show response if it was handled as structured data (JSON)
      if (!wasHandledAsStructuredData) {
        setShowResponse(true);
      }
    } catch (error) {
      handleError(error, enqueueSnackbar);
      setAssistantResponse("An error occurred while processing your request.");
      setShowResponse(true);
    } finally {
      setIsWorking(false);
      setIsRefreshing(false);
      // Clear input message after response is complete (but not during refresh)
      if (!isRefresh) {
        setInputMessage("");
      }
      // Wait longer to ensure the field is re-enabled before focusing
      setTimeout(() => {
        if (messageInputRef.current && !messageInputRef.current.disabled) {
          messageInputRef.current.focus();
        }
      }, 300);
    }
  };

  const refreshObjectFromAttachments = (currentRecord) => {
    Object.keys(currentRecord).forEach((key) => {
      if (currentRecord[key] === "null") {
        currentRecord[key] = null;
      }
    });

    // Check if this is the traditional structure (currentRecord has direct id field)
    if (currentRecord.id) {
      // Original logic for single record with id
      let foundAttachmentItem = null;

      if (attachments) {
        for (const attachment of attachments) {
          const attachmentRecord = attachment?.content?.find((a) => a.id === currentRecord.id);
          if (attachmentRecord) {
            foundAttachmentItem = attachmentRecord;
            break;
          }
        }
      }

      if (foundAttachmentItem) {
        const mergedRecord = { ...foundAttachmentItem };
        Object.keys(currentRecord).forEach((key) => {
          if (key !== "id") {
            mergedRecord[key] = currentRecord[key];
          }
        });
        return mergedRecord;
      } else {
        return currentRecord;
      }
    } else {
      // NEW BRANCH: Handle structure where top-level keys are attachment names containing arrays
      const updatedRecord = { ...currentRecord };

      Object.keys(currentRecord).forEach((attachmentName) => {
        const recordArray = currentRecord[attachmentName];
        if (Array.isArray(recordArray)) {
          updatedRecord[attachmentName] = recordArray.map((item) => {
            if (item.id && attachments) {
              // Find the attachment with the matching name
              const targetAttachment = attachments.find((attachment) => attachment.name === attachmentName);
              if (targetAttachment && targetAttachment.content) {
                // Look for the matching record in this specific attachment
                const attachmentRecord = targetAttachment.content.find((a) => a.id === item.id);
                if (attachmentRecord) {
                  const mergedRecord = { ...attachmentRecord };
                  Object.keys(item).forEach((key) => {
                    if (key !== "id") {
                      mergedRecord[key] = item[key];
                    }
                  });
                  return mergedRecord;
                }
              }
            }
            return item;
          });
        }
      });

      return updatedRecord;
    }
  };

  async function SendChatCompletion_api({ messages, attachments, onChunk }) {
    try {
      // Build request for /v1/responses API (camelCase, OpenAI-compatible)
      const request = {
        model: MODEL_NAME_POPUP_HELPER,
        messages: messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
        assistant: promptName || PROMPT_NAME_DEFAULT,
        stream: true, // Enable SSE streaming
        temperature: 0.7,
        reasoning: {
          effort: "medium",
        },
      };

      if (attachments && attachments.length > 0) {
        request.attachments = attachments.map((a) => ({
          name: a.name,
          content: typeof a.content === "string" ? a.content : JSON.stringify(a.content),
          doPreload: a.doPreload || false,
        }));
      }

      const sessionManager = new AmplifySessionManager();
      const jwt = await sessionManager.getBearerToken();

      const response = await fetch(import.meta.env.VITE_RGCHAT_API_URL_ROOT + `v1/responses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + jwt,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw {
          consoleMessage: `API Error: ${response.status} ${response.statusText}`,
          userMessage: `API Error: ${response.status} - ${response.statusText}`,
        };
      }

      // Parse SSE stream with chunk callback
      return await parseSSEStream(response, onChunk);
    } catch (error) {
      console.error("Network or Unexpected Error in SendChatCompletion_api:", error);
      enqueueSnackbar("A network or unexpected error occurred.", { preventDuplicate: true, variant: "error" });
      return null;
    }
  }

  async function parseSSEStream(response, onChunk) {
    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    let buffer = "";
    let contentResult = "";
    let reasoningResult = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();

            if (data === "[DONE]") {
              return { content: contentResult, reasoning: reasoningResult };
            }

            try {
              const parsed = JSON.parse(data);
              const delta = parsed.choices?.[0]?.delta;

              if (delta?.content) {
                contentResult += delta.content;
              }

              if (delta?.reasoning) {
                reasoningResult += delta.reasoning;
              }

              // Call onChunk callback with accumulated content for incremental updates
              if (onChunk && (delta?.content || delta?.reasoning)) {
                onChunk(contentResult, reasoningResult);
              }
            } catch (e) {
              console.error("Failed to parse SSE data:", e);
            }
          }
        }
      }

      return { content: contentResult, reasoning: reasoningResult };
    } finally {
      reader.releaseLock();
    }
  }

  const handleVoiceButtonClick = () => {
    if (listening) {
      onStopRecording();
      setChatVoiceModeOn(false);
      // Restore focus to input when stopping recording
      setTimeout(() => {
        if (messageInputRef.current && !messageInputRef.current.disabled) {
          messageInputRef.current.focus();
        }
      }, 200);
    } else {
      onStartRecording();
      setChatVoiceModeOn(true);
    }
  };

  const clearCommand = () => {
    setInputMessage("");
    setAssistantResponse("");
    setShowResponse(false);
    onStopRecording?.();
    // Restore focus to input
    setTimeout(() => {
      if (messageInputRef.current && !messageInputRef.current.disabled) {
        messageInputRef.current.focus();
      }
    }, 200);
  };

  const resendLastMessage = () => {
    if (lastSentMessage && lastSentMessage.trim()) {
      sendChatCompletion(lastSentMessage, true);
    }
  };

  const handleClose = () => {
    clearCommand();
    onClose?.();
  };

  const formatTime = (sec) => {
    const s = String(sec).padStart(2, "0");
    return `0:${s}`;
  };

  const clearResponse = () => {
    setShowResponse(false);
    setAssistantResponse("");
  };

  const handleClickAway = (event) => {
    // Don't close if clicking within the input area
    if (event.target.closest("input") || event.target.closest("textarea")) {
      return;
    }
    if (showResponse && !isWorking && !isRefreshing) {
      clearResponse();
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: "800px" }}>
      <Box sx={{ position: "relative" }}>
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: `linear-gradient(var(--mui-palette-background-paper), var(--mui-palette-background-paper)) padding-box, 
                         linear-gradient(to right, ${theme.palette.primary.light}, ${theme.palette.secondary.light}) border-box`,
            borderRadius: ".5em",
            border: "2px solid transparent",
            fontSize: "16px",
            overflow: "hidden",
          })}
        >
          {listening ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "56px",
                width: "100%",
                padding: "12px 10px 12px 16px",
              }}
            >
              <Typography sx={{ fontWeight: 500 }} color={elapsedTime > 22 ? "error" : "black"}>
                {formatTime(elapsedTime)}
              </Typography>
              <SoundWave sx={{ height: 29, width: 72 }} />
              <Box sx={{ display: "flex", gap: 4 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{
                    cursor: "pointer",
                    textTransform: "uppercase",
                    fontSize: "14px",
                    opacity: 0.7,
                  }}
                  onClick={() => {
                    onStopRecording();
                    setChatVoiceModeOn(false);
                  }}
                >
                  Cancel
                </Typography>
                <Typography
                  variant="overline"
                  sx={{
                    cursor: "pointer",
                    textTransform: "uppercase",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "primary.light",
                    backgroundColor: "rgba(128, 188, 0, 0.1)",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "primary.light",
                  }}
                  onClick={() => {
                    // Set flag to auto-submit when transcription completes
                    setShouldAutoSubmit(true);
                    onStopRecording();
                    setChatVoiceModeOn(false);
                  }}
                >
                  Send
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
              <TextField
                fullWidth
                variant="standard"
                placeholder={placeholder}
                value={inputMessage}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey) {
                    event.preventDefault();
                    if (inputMessage.trim()) {
                      sendChatCompletion(inputMessage);
                    }
                  }
                }}
                disabled={isWorking}
                inputRef={messageInputRef}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: "16px",
                    fontWeight: "500",
                    padding: "12px 16px",
                  },
                }}
              />

              <IconButton
                onClick={handleVoiceButtonClick}
                disabled={isWorking}
                sx={{
                  bgcolor: listening ? "error.main" : "primary.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: listening ? "error.dark" : "primary.dark",
                  },
                  width: "40px",
                  height: "40px",
                  mr: 1,
                }}
              >
                <MicrophoneIcon color="white" />
              </IconButton>

              <IconButton
                onClick={() => {
                  if (inputMessage.trim()) {
                    sendChatCompletion(inputMessage);
                  }
                }}
                disabled={isWorking || !inputMessage.trim()}
                sx={{
                  backgroundColor: "secondary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "secondary.dark",
                  },
                  "&:disabled": {
                    backgroundColor: "action.disabled",
                    color: "action.disabled",
                  },
                  width: "40px",
                  height: "40px",
                  mr: 1,
                }}
              >
                <SendIcon color="white" />
              </IconButton>

              <IconButton
                onClick={handleClose}
                sx={{
                  color: "text.secondary",
                  width: "40px",
                  height: "40px",
                  mr: 1,
                }}
              >
                <Close />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Floating "Tilley is thinking..." animation */}
        {isWorking && (
          <Fade in={isWorking} timeout={200}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "30%",
                transform: "translate(-30%, -50%)",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: "8px 16px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                border: "1px solid rgba(128, 188, 0, 0.2)",
                zIndex: 10,
              }}
            >
              <Typography
                variant="body2"
                color="secondary"
                sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "14px" }}
              >
                <Thinking />
                Tilley is thinking...
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Floating Assistant Response */}
      {showResponse && assistantResponse && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Fade in={showResponse} timeout={300}>
            <Paper
              elevation={8}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                mt: 1,
                p: 2,
                backgroundColor: "colors.backgroundLightCyan",
                borderLeft: "4px solid",
                borderLeftColor: "primary.light",
                zIndex: 1000,
                width: { xs: "100%", sm: "60vw" }, // Expand wider on larger screens but stay reasonable
                maxWidth: { xs: "100%", sm: "1200px" }, // Reasonable max width
              }}
            >
              <Stack direction="row" justifyContent="flex-end" alignItems="flex-start" sx={{ mb: 1, gap: 1 }}>
                <IconButton size="small" onClick={resendLastMessage} disabled={isWorking || !lastSentMessage}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={clearResponse}>
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
              <Box sx={{ position: "relative", overflow: "hidden" }}>
                {isRefreshing && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      p: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="95%" height={20} />
                    <Skeleton variant="text" width="85%" height={20} />
                    <Skeleton variant="text" width="92%" height={20} />
                    <Skeleton variant="rectangular" width="100%" height={60} sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="88%" height={20} />
                  </Box>
                )}
                <Box
                  sx={{
                    ...getMarkdownStyles(theme),
                    opacity: isRefreshing ? 0 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                  className="markdown-content"
                >
                  <ReactMarkdown components={getCustomMarkdownComponents()} remarkPlugins={[remarkGfm]} skipHtml={true}>
                    {assistantResponse}
                  </ReactMarkdown>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </ClickAwayListener>
      )}
    </Box>
  );
};
